import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, MapPin, Phone, Mail, Clock, Send, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ADDRESS, EMAIL, HOURS, PHONE, PHONE_HREF, BOOKINGS_EMAIL } from "@/lib/site";
import DatePicker from "@/components/DatePicker";

const MAP_EMBED_SRC = `https://www.google.com/maps?q=${encodeURIComponent(
  "Cool Runnings Toboggan Park, Off Carl Cronje Dr, Tygervalley, Cape Town, 7530, South Africa"
)}&z=16&hl=en&output=embed`;

const TYPES = [
  "General",
  "Corporate & Team Building",
  "School Outings & Educational Trips",
  "Birthday Parties & Bachelor Parties",
  "Gift Vouchers",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", enquiry_type: "General", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sentRef, setSentRef] = useState("");
  const [mapOpen, setMapOpen] = useState(false);
  const [visitDate, setVisitDate] = useState(undefined);
  const [agree, setAgree] = useState(false);
  const openedAt = useRef(Date.now());

  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const pre = location.state?.enquiryType;
    if (pre) {
      setForm((f) => ({ ...f, enquiry_type: pre }));
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone.trim().replace(/\D/g, "").length < 7) {
      toast.error("Please add your phone number — we often call to explain things quickly.");
      return;
    }
    if (!visitDate) {
      toast.error("Please pick your preferred visit date.");
      return;
    }
    setSending(true);
    try {
      const res = await api.post("/enquiries", {
        ...form,
        visit_date: visitDate ? visitDate.toISOString().slice(0, 10) : "",
        elapsed_ms: Date.now() - openedAt.current,
      });
      if (res.data?.email_sent) {
        toast.success(`Thank you! Confirmation email sent to ${form.email}.`);
      } else {
        toast.success("Thank you — we'll reply to your inbox soon.");
      }
      setSentRef(res.data?.ref || "");
      setAgree(false);
      setForm({ name: "", email: "", phone: "", enquiry_type: "General", message: "", website: "" });
      setVisitDate(undefined);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Something went wrong — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative border-t border-white/10 bg-[#141416] py-24" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="mt-3 text-4xl leading-tight text-[#f8b818] drop-shadow-[0_2px_0_rgba(122,86,0,0.7)] sm:text-5xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>LET'S GET YOU ON THE TRACK</h2>

          <form onSubmit={submit} className="contact-form mt-8 space-y-4" data-testid="contact-form">
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute -left-[9999px] h-0 w-0 opacity-0"
              data-testid="contact-website-hp"
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <input
                className="input-dark"
                placeholder="Your name *"
                required
                minLength={2}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="contact-form-name-input"
              />
              <input
                className="input-dark"
                type="email"
                placeholder="Email address *"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="contact-form-email-input"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input
                className="input-dark"
                type="tel"
                placeholder="Phone number *"
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="contact-form-phone-input"
              />
              <select
                className="input-dark"
                value={form.enquiry_type}
                onChange={(e) => setForm({ ...form, enquiry_type: e.target.value })}
                data-testid="contact-form-type-select"
              >
                {(TYPES.includes(form.enquiry_type) || !form.enquiry_type ? TYPES : [form.enquiry_type, ...TYPES]).map((t) => (
                  <option key={t} value={t} className="bg-[#141416]">
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-[#8a7b5e]">
                Preferred visit date <span className="text-[#f8b818]">*</span>
              </p>
              <DatePicker
                value={visitDate}
                onChange={setVisitDate}
                placeholder="Pick a day on the calendar"
                testid="contact-form-date-picker"
              />
            </div>
            <textarea
              className="input-dark min-h-32"
              placeholder="Tell us about your visit or event *"
              required
              minLength={10}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="contact-form-message-textarea"
            />
            {sentRef && (
              <div className="rounded-2xl border border-green-600/40 bg-[#faf5ea] p-4" data-testid="contact-success-panel">
                <p className="text-sm font-extrabold text-green-700">You're on the list, thank you!</p>
                <p className="mt-1 text-xs leading-relaxed text-[#241c0d]">
                  Your enquiry is with our team — keep an eye on your inbox for the confirmation, we usually reply
                  within a day.
                </p>
              </div>
            )}
            <label className="flex items-start gap-2.5 text-xs leading-relaxed text-[#f5ead0]" data-testid="contact-terms-row">
              <input
                type="checkbox"
                required
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#f8b818]"
                data-testid="contact-terms-tick"
              />
              <span>
                I've read and agree to the Cool Runnings{" "}
                <Link to="/safety" className="font-extrabold text-[#f8b818] underline underline-offset-2 hover:text-[#f9c92e]" data-testid="contact-terms-link">
                  Terms and Conditions
                </Link>{" "}
                on our Safety page.
              </span>
            </label>
            <button type="submit" disabled={sending} className="btn-gold w-full disabled:opacity-60" data-testid="contact-form-submit-btn">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
              {sending ? "Sending…" : "Send enquiry"}
            </button>
            <p className="text-xs text-[#8a7b5e]">
              You'll get an instant confirmation email. We process personal info in line with POPIA.
            </p>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="space-y-4 self-start"
        >
          <div className="card-dark flex items-start gap-4 p-6" data-testid="contact-info-address">
            <span className="rounded-2xl border border-[#241c0d]/15 bg-[#f8b818]/15 p-3 text-[#c87700]">
              <MapPin size={20} />
            </span>
            <div>
              <p className="text-xs font-extrabold uppercase tracking-widest text-[#8a7b5e]">Find us</p>
              <button
                onClick={() => setMapOpen(!mapOpen)}
                aria-expanded={mapOpen}
                className="mt-1 flex items-center gap-1.5 text-left font-bold transition-colors hover:text-[#f8b818]"
                data-testid="contact-map-toggle-btn"
              >
                {ADDRESS}
                <ChevronDown
                  size={14}
                  className={`shrink-0 transition-transform duration-300 ${mapOpen ? "rotate-180" : ""}`}
                />
              </button>
            </div>
          </div>

          <AnimatePresence>
            {mapOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
                data-testid="contact-map-dropdown"
              >
                <div className="overflow-hidden rounded-3xl border border-[#f8b818]/40 bg-white shadow-[0_10px_30px_rgba(248,184,24,0.18)]">
                  <iframe
                    src={MAP_EMBED_SRC}
                    title="Google Map showing Toboggan Family Park (Pty) Ltd., t/a Cool Runnings in Tygervalley, Cape Town"
                    className="h-[300px] w-full border-0"
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="contact-map-embed"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="card-dark flex items-start gap-4 p-6" data-testid="contact-info-email">
            <span className="rounded-2xl border border-[#241c0d]/15 bg-[#f8b818]/15 p-3 text-[#c87700]">
              <Mail size={20} />
            </span>
            <div className="space-y-1.5">
              <p className="text-xs font-extrabold uppercase tracking-widest text-[#8a7b5e]">Email us</p>
              <p className="text-sm font-bold">
                General emails:{" "}
                <a href={`mailto:${EMAIL}`} className="font-extrabold hover:text-[#f8b818]">
                  {EMAIL}
                </a>
              </p>
              <p className="text-sm font-bold">
                Bookings:{" "}
                <a href={`mailto:${BOOKINGS_EMAIL}`} className="font-extrabold hover:text-[#f8b818]">
                  {BOOKINGS_EMAIL}
                </a>
              </p>
            </div>
          </div>

          {[
            { icon: Phone, label: "Call the park", value: PHONE, href: PHONE_HREF, testid: "contact-info-phone" },
            { icon: Clock, label: "Opening hours", value: `Mon–Fri ${HOURS.mon_fri} · Weekends, Public- & School Holidays ${HOURS.sat_sun}`, testid: "contact-info-hours" },
          ].map((c) => (
            <div key={c.label} className="card-dark flex items-start gap-4 p-6" data-testid={c.testid}>
              <span className="rounded-2xl border border-[#241c0d]/15 bg-[#f8b818]/15 p-3 text-[#c87700]">
                <c.icon size={20} />
              </span>
              <div>
                <p className="text-xs font-extrabold uppercase tracking-widest text-[#8a7b5e]">{c.label}</p>
                {c.href ? (
                  <a href={c.href} target="_blank" rel="noopener noreferrer" className="mt-1 block font-bold hover:text-[#f8b818]">
                    {c.value}
                  </a>
                ) : (
                  <p className="mt-1 font-bold">{c.value}</p>
                )}
              </div>
            </div>
          ))}

          <div className="card-dark flex items-start gap-4 p-6" data-testid="contact-official-channels">
            <span className="rounded-2xl border border-[#241c0d]/15 bg-[#f8b818]/15 p-3 text-[#c87700]">
              <ShieldCheck size={20} />
            </span>
            <div>
              <p className="text-xs font-extrabold uppercase tracking-widest text-[#8a7b5e]">Official channels only</p>
              <p className="mt-1 text-sm leading-relaxed">
                The addresses and number on this page are our only accounts. We never ask for card details, PINs or
                payments by email or WhatsApp — if someone contacts you claiming to be us, please call the park to
                check first.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
